import { isEqual } from 'lodash';
import { CellSelections } from '../../../../../../common/table/TableTypes';
import { getEndCol, getEndRow, getStartCol, getStartRow } from '../../selectionUtils/tableSelectionAccessors';
import { isSelectingRefColHeader, isSelectingRefRowHeader } from '../../selectionUtils/tableSelectionPredicateUtils';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

type UpdateHotTableCellSelectionOptions = {
    changeListener?: boolean;
};

/**
 * Moves the cell selections in the Handsontable instance to match the provided selections.
 */
export const updateHotTableCellSelections = (
    hotInstance: MNHotInstance | null | undefined,
    cellSelections: CellSelections,
    options: UpdateHotTableCellSelectionOptions = {},
) => {
    if (!hotInstance) return;

    const { changeListener = true } = options;

    hotInstance.deselectCell();

    if (!cellSelections) return;

    const currentHotCellSelections = hotInstance.getSelected();
    if (isEqual(cellSelections, currentHotCellSelections)) return;

    const selectingRefColHeader = cellSelections.find(isSelectingRefColHeader);
    if (selectingRefColHeader) {
        hotInstance.selectColumns(
            Math.max(getStartCol(selectingRefColHeader), 0),
            Math.min(getEndCol(selectingRefColHeader), hotInstance.countCols() - 1),
        );
        return;
    }

    const selectingRefRowHeader = cellSelections.find(isSelectingRefRowHeader);
    if (selectingRefRowHeader) {
        hotInstance.selectRows(
            Math.max(getStartRow(selectingRefRowHeader), 0),
            Math.min(getEndRow(selectingRefRowHeader), hotInstance.countRows() - 1),
        );
        return;
    }

    hotInstance.selectCells(cellSelections, true, changeListener);
};

/**
 * Selects the first table cell in the active Handsontable instance.
 */
export const selectFirstHotTableCell = (
    hotInstance: MNHotInstance | null | undefined,
    options?: UpdateHotTableCellSelectionOptions,
): void => updateHotTableCellSelections(hotInstance, [[0, 0, 0, 0]], options);
